/**
 * Os códigos da trilha em português — Frente L, Story L.4.
 *
 * ## Por que é um módulo à parte
 *
 * Traduzir código em frase é a única regra desta tela, e é a que mais vai
 * mudar: cada ação nova no back acrescenta uma linha aqui. Isolado, dá para
 * testá-lo sem montar componente nenhum — e um teste de frase que precisa
 * renderizar a página inteira acaba testando a página, não a frase.
 *
 * ## A chave é o PAR `entidade:acao`, não um código único
 *
 * O back grava `(entidade, acao)` — `quote` + `emitir`, `quote_item` + `criar`.
 * O mesmo verbo aparece em entidades diferentes: `excluir` é "apagou o
 * rascunho" em `quote` e "removeu um item" em `quote_item`. Só o par
 * desambigua, e foi por isso que a L.2 manteve esse formato em vez de um
 * namespace na coluna `acao`.
 *
 * Os eventos de segurança não têm entidade: a chave deles é o `tipo` puro.
 */

import { rotuloDeNivel } from '@orc/shared';

/** Uma entrada da trilha, como a L.3 devolve. */
export interface EntradaLog {
  id: string;
  quando: string;
  origem: 'audit' | 'security';
  codigo: string;
  codigoBruto: string;
  entidade: string | null;
  entidadeId: string | null;
  autorId: string | null;
  autorNome: string | null;
  formatoAntigo: boolean;
  detalhe: { antes?: Record<string, unknown>; depois?: Record<string, unknown> };
}

/**
 * As frases de auditoria, por `entidade:acao`.
 *
 * Cada uma recebe o registro e pode olhar o `detalhe` — é assim que "emitiu o
 * orçamento" vira "emitiu o orçamento ORC-2026-0001".
 */
const FRASES_AUDIT: Record<string, (e: EntradaLog) => string> = {
  'quote:emitir': (e) => `emitiu o orçamento ${numero(e) ?? '(sem número)'}`,
  'quote:parametros': (e) => `alterou o ${queParametro(e)} do orçamento`,
  /*
   * S8 — ação própria, não um `parametros`: o sentido da troca é o conteúdo.
   * Registro sem o campo (não deveria existir) cai na frase neutra, em vez de
   * afirmar um sentido que o log não diz.
   */
  'quote:ipi': (e) => {
    const depois = e.detalhe.depois?.['aplicarIpi'];
    if (depois === false) return 'deixou de aplicar o IPI no orçamento';
    if (depois === true) return 'voltou a aplicar o IPI no orçamento';
    return 'alterou o IPI do orçamento';
  },
  'quote:excluir': () => 'apagou um rascunho de orçamento',
  'quote:criar': () => 'criou um rascunho de orçamento',
  'quote_item:criar': () => 'adicionou um item ao orçamento',
  'quote_item:excluir': () => 'removeu um item do orçamento',
  'quote_item:editar': (e) => `editou um item do orçamento${queCampos(e)}`,
  'quote_item:nivel_del': (e) => `alterou o nível Del Credere de um item${deParaNivel(e)}`,
  'user:criar': (e) => `criou o usuário ${alvo(e) ?? ''}`.trim(),
  'user:editar': (e) =>
    trocouSenha(e) ? 'trocou a senha' : `editou o usuário ${alvo(e) ?? ''}`.trim(),
  'user:inativar': (e) => `desativou o usuário ${alvo(e) ?? ''}`.trim(),
  'user:reativar': (e) => `reativou o usuário ${alvo(e) ?? ''}`.trim(),
  'product:criar': (e) => `cadastrou o produto ${referencia(e) ?? ''}`.trim(),
  'product:editar': (e) => `editou o produto ${referencia(e) ?? ''}`.trim(),
  'product:inativar': (e) => `desativou o produto ${referencia(e) ?? ''}`.trim(),
  'product:reativar': (e) => `reativou o produto ${referencia(e) ?? ''}`.trim(),
  'product_price:criar': () => 'definiu o preço de um produto',
  'product_price:editar': () => 'alterou o preço de um produto',
  'price_table_version:criar': () => 'enviou uma planilha de preços',
  'price_table_version:efetivar': () => 'EFETIVOU uma tabela de preços',
  'price_table_version:rejeitar': (e) => `teve uma importação recusada${motivo(e)}`,
  'commercial_param:editar': () => 'alterou o RT/frete padrão da empresa',
  'pricing_rule:editar': (e) => `trocou a coluna de preço${daClasse(e)}${deParaFonte(e)}`,
};

/** As frases de segurança, pelo `tipo`. */
const FRASES_SECURITY: Record<string, (e: EntradaLog) => string> = {
  login_ok: () => 'entrou no sistema',
  login_fail: (e) => `tentativa de login falha${quemTentou(e)}`,
  logout: () => 'saiu do sistema',
  senha_trocada: () => 'trocou a senha',
  senha_troca_negada: (e) => `tentou trocar a senha e não conseguiu${motivo(e)}`,
  acesso_negado: (e) =>
    `teve acesso negado a ${String(e.detalhe.depois?.['rota'] ?? 'uma área restrita')}`,
  tenant_mismatch: () => 'usou um token de outra empresa',
  rate_limit: () => 'excedeu o limite de tentativas',
  refresh_ok: () => 'renovou a sessão',
  /*
   * `refresh_reuso` é o único evento de segurança que a tela marca como grave.
   * Reuso de token já revogado é a assinatura de sessão roubada — a L.3 o
   * deixou de fora da lista de "rotina" pelo mesmo motivo.
   */
  refresh_reuso: () => 'reuso de token revogado (suspeito)',
};

// ------------------------------------------------------------------ auxiliares

function texto(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() !== '' ? v : undefined;
}

function numero(e: EntradaLog): string | undefined {
  return texto(e.detalhe.depois?.['numero']);
}

function alvo(e: EntradaLog): string | undefined {
  return texto(e.detalhe.depois?.['nome']) ?? texto(e.detalhe.antes?.['nome']);
}

function referencia(e: EntradaLog): string | undefined {
  return texto(e.detalhe.depois?.['referencia']) ?? texto(e.detalhe.antes?.['referencia']);
}

/**
 * Motivos que o log grava como código. Sem isto a tela de atividade mostra o
 * identificador cru — `cabecalho_divergente` — para quem só quer saber o que
 * houve. Motivo não mapeado cai no próprio código, que é melhor que sumir.
 */
const MOTIVOS_LEGIVEIS: Record<string, string> = {
  planilha_invalida: 'a planilha nao pôde ser lida',
  cabecalho_divergente: 'o cabeçalho da planilha não é o esperado',
};

function motivo(e: EntradaLog): string {
  const m = texto(e.detalhe.depois?.['motivo']);
  if (m === undefined) return '';
  return ` — ${MOTIVOS_LEGIVEIS[m] ?? m}`;
}

function trocouSenha(e: EntradaLog): boolean {
  return e.detalhe.depois?.['senhaTrocada'] === true;
}

/** Diz SE foi RT, frete ou os dois — a informação está no que mudou. */
function queParametro(e: EntradaLog): string {
  const d = e.detalhe.depois ?? {};
  const rt = 'rtPercentManual' in d;
  const frete = 'freteRsKgManual' in d;
  if (rt && frete) return 'RT e o frete';
  if (frete) return 'frete';
  return 'RT';
}

/**
 * "(quantidade)" ou "(preço e quantidade)" — o campo `mudou` que a L.2 grava.
 *
 * Sem isto, toda edição de item vira a mesma frase e a trilha não distingue
 * "corrigiu a quantidade" de "mexeu no preço fora da regra" — que é a diferença
 * que faz alguém abrir esta tela.
 */
function queCampos(e: EntradaLog): string {
  const mudou = texto(e.detalhe.depois?.['mudou']);
  if (mudou === undefined) return '';
  const nomes: Record<string, string> = {
    valor: 'preço',
    motivo: 'justificativa',
    quantidade: 'quantidade',
    desconto: 'desconto',
  };
  const partes = mudou
    .split('_e_')
    .map((p) => (p === 'ambiente' ? qualAmbiente(e) : (nomes[p] ?? p)));
  return ` (${partes.join(', ')})`;
}

/**
 * `ambiente → "COZINHA"` ou `ambiente removido` — S9.
 *
 * O valor vai na frase porque, ao contrário de um preço, ele não aparece em
 * nenhum outro lugar da trilha: "editou o ambiente" sem dizer qual não responde
 * nada a quem procura quem trocou a cozinha pelo banho.
 */
function qualAmbiente(e: EntradaLog): string {
  // `texto()` já trata vazio como ausente — e vazio nunca é gravado (vira nulo).
  const novo = texto(e.detalhe.depois?.['ambiente']);
  return novo === undefined ? 'ambiente removido' : `ambiente → "${novo}"`;
}

/** `de villa_arte` — a classe cuja coluna de preço mudou (S3b). */
function daClasse(e: EntradaLog): string {
  const c = texto(e.detalhe.depois?.['classe']) ?? texto(e.detalhe.antes?.['classe']);
  return c === undefined ? '' : ` de ${c}`;
}

/**
 * `(del_credere_5_15 → 5,5% · DEL 15%)` — a troca de coluna, em rótulo.
 *
 * As linhas vêm por quadrante (CPF e RT10 andam juntos). Quando todas fazem a
 * mesma troca — o caso normal — sai uma frase só; se divergirem, some, porque
 * mostrar a primeira daria a entender que vale para todas.
 */
function deParaFonte(e: EntradaLog): string {
  const fontes = (lado: 'antes' | 'depois'): string[] => {
    const linhas = e.detalhe[lado]?.['linhas'];
    if (!Array.isArray(linhas)) return [];
    return linhas
      .map((l) =>
        l === null || typeof l !== 'object'
          ? undefined
          : texto((l as Record<string, unknown>)['fonte']),
      )
      .filter((f): f is string => f !== undefined);
  };
  const de = [...new Set(fontes('antes'))];
  const para = [...new Set(fontes('depois'))];
  if (de.length !== 1 || para.length !== 1) return '';
  return ` (${rotuloNivel(de[0])} → ${rotuloNivel(para[0])})`;
}

function deParaNivel(e: EntradaLog): string {
  const de = texto(e.detalhe.antes?.['delNivel']);
  const para = texto(e.detalhe.depois?.['delNivel']);
  if (de === undefined && para === undefined) return '';
  return ` (${rotuloNivel(de)} → ${rotuloNivel(para)})`;
}

/**
 * O rótulo de um nível de preço, **derivado da fonte única** (`@orc/shared`).
 *
 * ## O que havia aqui, e por que estava errado
 *
 * A versão anterior tirava o percentual do número que está no nome da chave:
 * `del_3` → "3%". Mas `del_3` é a coluna de comissão **7%** — os nomes são
 * chaves posicionais opacas, e o número neles é resíduo histórico. Os seis
 * rótulos saíam errados.
 *
 * E o único que tinha caso especial errava junto, trocando o par: devolvia
 * "5,5%" para `del_55` (que é 5%) e "5%" para `del_5` (que é 5,5%).
 *
 * A causa foi ter reescrito o pareamento em vez de consumi-lo — a segunda cópia
 * que diverge, que é o mesmo defeito que produziu o preço errado do Villa Arte.
 * Agora há uma definição só, e ela serve o servidor e o navegador.
 */
function rotuloNivel(n: string | undefined): string {
  return rotuloDeNivel(n) ?? 'padrão';
}

/**
 * O e-mail de quem tentou entrar.
 *
 * A L.3 o entrega de propósito: sem ele, "tentativa de login falha" não separa
 * "alguém nosso digitou errado" de "estão varrendo contas". A rota é admin-only.
 */
function quemTentou(e: EntradaLog): string {
  const email = texto(e.detalhe.depois?.['email']);
  if (email !== undefined) return ` — ${email}`;
  const m = texto(e.detalhe.depois?.['motivo']);
  return m === 'usuario_inativo' ? ' — conta desativada' : '';
}

// --------------------------------------------------------------------- API

/**
 * A frase de uma entrada, em português.
 *
 * ## Código desconhecido não some da tela
 *
 * O fallback mostra o código cru entre parênteses. Uma ação nova no back que
 * ninguém traduziu aqui aparece feia — e aparecer feia é infinitamente melhor
 * do que sumir: numa trilha de auditoria, o registro invisível é o que causa
 * dano. É também o sinal de que falta uma linha neste arquivo.
 */
export function frase(e: EntradaLog): string {
  if (e.origem === 'security') {
    const f = FRASES_SECURITY[e.codigo];
    return f === undefined ? `evento de segurança (${e.codigo})` : f(e);
  }

  /*
   * O registro em FORMATO ANTIGO que a L.3 não conseguiu desambiguar continua
   * com `codigo: 'editar'`. Rotular honestamente é o requisito: a trilha não
   * sabe o que aquele `editar` fez, e inventar um detalhe seria pior do que
   * admitir a lacuna.
   */
  if (e.formatoAntigo && e.codigo === 'editar' && e.entidade === 'quote') {
    return 'editou o orçamento (registro antigo)';
  }

  const f = FRASES_AUDIT[`${e.entidade ?? ''}:${e.codigo}`];
  if (f === undefined) return `ação ${e.codigo} em ${e.entidade ?? 'algo'}`;

  /*
   * O registro antigo DESAMBIGUADO ganha a frase certa mais um aviso: a L.3
   * inferiu o código pelo conteúdo do JSON, não leu da coluna. É correto, e o
   * leitor merece saber que é inferência.
   */
  return e.formatoAntigo ? `${f(e)} (registro antigo)` : f(e);
}

/**
 * Os códigos oferecidos no filtro, já traduzidos.
 *
 * Lista fixa, e não derivada do que a página recebeu: um filtro que só mostra o
 * que já está na tela é inútil justamente quando se procura o que não está —
 * "houve alguma tentativa de login falha este mês?" precisa da opção mesmo
 * quando a resposta é não.
 */
export const CODIGOS_PARA_FILTRO: readonly { codigo: string; rotulo: string }[] = [
  { codigo: 'emitir', rotulo: 'Emissão de orçamento' },
  { codigo: 'parametros', rotulo: 'RT/frete do orçamento' },
  { codigo: 'ipi', rotulo: 'IPI do orçamento' },
  { codigo: 'criar', rotulo: 'Criação (item, usuário, produto…)' },
  { codigo: 'editar', rotulo: 'Edição' },
  { codigo: 'excluir', rotulo: 'Exclusão' },
  { codigo: 'nivel_del', rotulo: 'Nível Del Credere' },
  { codigo: 'efetivar', rotulo: 'Efetivação de tabela de preço' },
  { codigo: 'rejeitar', rotulo: 'Importação recusada' },
  { codigo: 'inativar', rotulo: 'Desativação' },
  { codigo: 'reativar', rotulo: 'Reativação' },
  { codigo: 'login_ok', rotulo: 'Entrada no sistema' },
  { codigo: 'login_fail', rotulo: 'Tentativa de login falha' },
  { codigo: 'logout', rotulo: 'Saída do sistema' },
  { codigo: 'senha_trocada', rotulo: 'Troca de senha' },
  { codigo: 'senha_troca_negada', rotulo: 'Troca de senha negada' },
  { codigo: 'acesso_negado', rotulo: 'Acesso negado' },
  { codigo: 'refresh_reuso', rotulo: 'Reuso de token (suspeito)' },
  { codigo: 'rate_limit', rotulo: 'Excesso de tentativas' },
  { codigo: 'refresh_ok', rotulo: 'Renovação de sessão (rotina)' },
];

/**
 * Eventos que merecem destaque visual.
 *
 * Curta de propósito: se muita coisa é grave, nada é. `refresh_reuso` é
 * suspeita de sessão roubada, e `efetivar` troca o preço de todo orçamento em
 * rascunho — a operação de maior alcance do sistema.
 */
export function ehGrave(e: EntradaLog): boolean {
  return (
    e.codigo === 'refresh_reuso' ||
    (e.entidade === 'price_table_version' && e.codigo === 'efetivar')
  );
}

/** `27/08/2026 14:32` — data e hora, que numa trilha é o que se procura. */
export function dataHoraBr(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
